import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useCurrency } from '../context/CurrencyContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/* ═══════════════════════════════════════════
   REEL CARD — video autoplay on scroll
═══════════════════════════════════════════ */
const ReelCardItem = ({ r, loadingProducts, onShopNow, modalOpen }) => {
  const videoRef = useRef(null);
  const cardRef  = useRef(null);

  // IntersectionObserver — play when card enters viewport
  useEffect(() => {
    const video = videoRef.current;
    const card  = cardRef.current;
    if (!video || !card) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !modalOpen) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(card);
    return () => observer.disconnect();
  }, [modalOpen]);

  // Pause all videos when modal opens, resume when it closes
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (modalOpen) {
      video.pause();
    } else {
      video.play().catch(() => {});
    }
  }, [modalOpen]);

  const hasVideo = !!r.video_url;

  return (
    <div ref={cardRef} className="sbr-card">
      <div className="sbr-img-wrap">

        {hasVideo ? (
          <video
            ref={videoRef}
            src={r.video_url}
            muted
            loop
            playsInline
            poster={r.cover_url || undefined}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center top',
              display: 'block',
              transition: 'transform 0.5s ease',
            }}
          />
        ) : (
          <img src={r.cover_url} alt={r.title} loading="lazy" />
        )}

        {/* Reel icon */}
        <span className="sbr-reel-icon">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 7l-1.5-2H7.5L6 7H3a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-3zm-6 10a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm0-6a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
          </svg>
        </span>
      </div>

      {/* Handle */}
      <span className="sbr-handle">{r.handle}</span>

      {/* Title */}
      <div className="sbr-card-title">{r.title}</div>

      {/* Shop Now */}
      <div className="sbr-btns">
        <button
          className="sbr-btn-shop"
          disabled={loadingProducts}
          onClick={e => onShopNow(e, r)}
        >
          {loadingProducts ? 'Loading…' : 'Shop Now'}
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
const ShopByReel = () => {
  const trackRef   = useRef(null);
  const animRef    = useRef(null);
  const posRef     = useRef(0);
  const pauseRef   = useRef(false);
  const sectionRef = useRef(null);
  const navigate   = useNavigate();

  const [reels, setReels]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeReel, setActiveReel] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const { formatPrice }             = useCurrency();

  /* ── Fetch reels ── */
  useEffect(() => {
    fetch(`${API}/reels`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setReels(
            data.reels
              .filter(r => r.is_active)
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          );
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  /* ── Pause auto-scroll when section leaves viewport ── */
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!activeReel) {
          pauseRef.current = !entry.isIntersecting;
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, [activeReel]);

  /* ── Auto-scroll animation ── */
  useEffect(() => {
    if (reels.length <= 1) return;
    const track = trackRef.current;
    if (!track) return;

    const cardWidth  = 220 + 14;
    const totalWidth = cardWidth * reels.length;

    const animate = () => {
      if (!pauseRef.current) {
        posRef.current += 0.3;
        if (posRef.current >= totalWidth / (reels.length > 2 ? 2 : 1)) {
          posRef.current = 0;
        }
        track.style.transform = `translateX(-${posRef.current}px)`;
      }
      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, [reels]);

  /* ── Shop Now → fetch products ── */
  const handleShopNow = async (e, reel) => {
    e.stopPropagation();
    pauseRef.current = true;
    setLoadingProducts(true);
    try {
      const res  = await fetch(`${API}/reels/${reel.id}/products`);
      const data = await res.json();
      setActiveReel({
        ...reel,
        products: data.success ? data.products : [],
      });
    } catch {
      setActiveReel({ ...reel, products: [] });
    } finally {
      setLoadingProducts(false);
    }
  };

  const closeModal = useCallback(() => {
    setActiveReel(null);
    pauseRef.current = false;
  }, []);

  /* ── Duplicate for infinite scroll ── */
  const DOUBLED = reels.length > 2 ? [...reels, ...reels] : reels;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');

        .sbr-section {
          background: #FAF8F4;
          padding: 80px 0 70px;
          overflow: hidden;
        }

        /* ── Header ── */
        .sbr-header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          padding: 0 clamp(24px, 5vw, 80px);
          margin-bottom: 40px;
          flex-wrap: wrap;
          gap: 16px;
        }
        .sbr-label {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #B08D57;
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .sbr-label::before {
          content: '';
          width: 28px; height: 1px;
          background: #B08D57;
          display: inline-block;
        }
        .sbr-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(28px, 4vw, 46px);
          font-weight: 700;
          color: #1C1A18;
          line-height: 1.08;
          letter-spacing: -0.01em;
        }
        .sbr-title em {
          font-style: italic;
          font-weight: 400;
          color: #B08D57;
        }
        .sbr-viewall {
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #fff;
          background: #1C1A18;
          text-decoration: none;
          padding: 13px 30px;
          display: inline-block;
          transition: background 0.22s;
          white-space: nowrap;
        }
        .sbr-viewall:hover { background: #B08D57; }

        /* ── Scroll track ── */
        .sbr-overflow { overflow: hidden; width: 100%; }
        .sbr-track {
          display: flex;
          gap: 14px;
          width: max-content;
          will-change: transform;
        }

        /* ── Reel card ── */
        .sbr-card {
          flex-shrink: 0;
          width: 220px;
          position: relative;
          cursor: default;
          border-radius: 2px;
          overflow: hidden;
          background: #1C1A18;
        }
        .sbr-img-wrap {
          position: relative;
          width: 100%;
          aspect-ratio: 9/16;
          overflow: hidden;
        }
        .sbr-img-wrap img,
        .sbr-img-wrap video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center top;
          display: block;
          transition: transform 0.5s ease;
        }
        .sbr-card:hover .sbr-img-wrap img,
        .sbr-card:hover .sbr-img-wrap video { transform: scale(1.04); }
        .sbr-img-wrap::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to top,
            rgba(28,26,24,0.92) 0%,
            rgba(28,26,24,0.3) 50%,
            transparent 100%
          );
          z-index: 1;
        }

        /* reel icon */
        .sbr-reel-icon {
          position: absolute;
          top: 10px; right: 10px;
          z-index: 3;
          width: 24px; height: 24px;
          opacity: 0.8;
        }
        .sbr-reel-icon svg { width: 100%; height: 100%; fill: #fff; }

        /* handle */
        .sbr-handle {
          position: absolute;
          bottom: 120px; left: 12px;
          z-index: 3;
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 400;
          color: rgba(255,255,255,0.6);
          letter-spacing: 0.06em;
        }

        /* title */
        .sbr-card-title {
          position: absolute;
          bottom: 66px; left: 12px; right: 12px;
          z-index: 3;
          font-family: 'Jost', sans-serif;
          font-size: 12px;
          font-weight: 500;
          color: #fff;
          line-height: 1.4;
          letter-spacing: 0.02em;
        }

        /* Shop Now */
        .sbr-btns {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          z-index: 3;
          padding: 0 10px 14px;
        }
        .sbr-btn-shop {
          width: 100%;
          background: #B08D57;
          border: none;
          color: #fff;
          font-family: 'Jost', sans-serif;
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          padding: 11px;
          cursor: pointer;
          display: block;
          text-align: center;
          transition: background 0.2s;
        }
        .sbr-btn-shop:hover { background: #8A6D3E; }
        .sbr-btn-shop:disabled { opacity: 0.7; cursor: not-allowed; }

        /* loading */
        .sbr-loading {
          text-align: center;
          padding: 60px;
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          color: #9A9186;
          letter-spacing: 0.1em;
        }

        /* ══════════════ MODAL ══════════════ */
        .srm-backdrop {
          position: fixed;
          inset: 0;
          z-index: 10000;
          background: rgba(16,14,12,0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          backdrop-filter: blur(6px);
        }
        .srm-modal {
          background: #FAF8F4;
          width: 100%;
          max-width: 860px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          position: relative;
          box-shadow: 0 40px 100px rgba(0,0,0,0.45);
          overflow: hidden;
        }
        .srm-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 26px 18px;
          border-bottom: 1px solid rgba(176,141,87,0.18);
          background: #fff;
          flex-shrink: 0;
        }
        .srm-eyebrow {
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.26em;
          text-transform: uppercase;
          color: #B08D57;
          margin-bottom: 4px;
        }
        .srm-head-title {
          font-family: 'Playfair Display', serif;
          font-size: 20px;
          font-weight: 700;
          color: #1C1A18;
          line-height: 1.15;
        }
        .srm-head-title em { font-style: italic; font-weight: 400; color: #B08D57; }
        .srm-close {
          width: 34px; height: 34px;
          border-radius: 50%;
          background: #1C1A18;
          border: none;
          color: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
          flex-shrink: 0;
        }
        .srm-close:hover { background: #B08D57; }
        .srm-body {
          flex: 1;
          overflow-y: auto;
          padding: 24px 26px 28px;
          scrollbar-width: thin;
          scrollbar-color: #e0d8cc transparent;
        }
        .srm-body::-webkit-scrollbar { width: 4px; }
        .srm-body::-webkit-scrollbar-thumb { background: #e0d8cc; border-radius: 2px; }
        .srm-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 14px;
        }
        .srm-card {
          background: #fff;
          border: 1px solid rgba(176,141,87,0.1);
          cursor: pointer;
          transition: box-shadow 0.25s, transform 0.22s;
          overflow: hidden;
        }
        .srm-card:hover {
          box-shadow: 0 10px 32px rgba(28,26,24,0.10);
          transform: translateY(-3px);
        }
        .srm-card-img {
          position: relative;
          width: 100%;
          aspect-ratio: 1/1;
          overflow: hidden;
          background: #f5f0ea;
        }
        .srm-card-img img {
          width: 100%; height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.5s ease;
        }
        .srm-card:hover .srm-card-img img { transform: scale(1.06); }
        .srm-card-badge {
          position: absolute;
          top: 8px; left: 8px;
          font-family: 'Jost', sans-serif;
          font-size: 8px;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          padding: 4px 9px;
          z-index: 2;
        }
        .srm-card-body { padding: 11px 12px 13px; }
        .srm-card-name {
          font-family: 'Playfair Display', serif;
          font-size: 13px;
          font-weight: 600;
          color: #1C1A18;
          line-height: 1.3;
          margin-bottom: 7px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .srm-card-prices {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          margin-bottom: 10px;
        }
        .srm-card-price {
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          font-weight: 700;
          color: #B08D57;
        }
        .srm-card-mrp { font-size: 11px; color: #b0a99e; text-decoration: line-through; }
        .srm-card-off {
          font-size: 8px;
          font-weight: 700;
          color: #C0392B;
          background: rgba(192,57,43,0.08);
          padding: 2px 5px;
          letter-spacing: 0.08em;
        }
        .srm-card-btn {
          width: 100%;
          background: #1C1A18;
          color: #fff;
          border: none;
          font-family: 'Jost', sans-serif;
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          padding: 10px;
          cursor: pointer;
          transition: background 0.2s;
        }
        .srm-card-btn:hover { background: #B08D57; }
        .srm-foot {
          padding: 14px 26px;
          border-top: 1px solid rgba(176,141,87,0.15);
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-shrink: 0;
        }
        .srm-foot-count { font-size: 11px; color: #8A7E72; letter-spacing: 0.06em; }
        .srm-foot-count span { color: #1C1A18; font-weight: 600; }
        .srm-view-all {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          background: #1C1A18;
          color: #fff;
          border: none;
          padding: 12px 28px;
          cursor: pointer;
          text-decoration: none;
          display: inline-block;
          transition: background 0.2s;
        }
        .srm-view-all:hover { background: #B08D57; }
        .srm-empty {
          text-align: center;
          padding: 60px 20px;
          color: #9A9186;
          font-size: 13px;
          font-style: italic;
        }

        @media (max-width: 700px) {
          .srm-grid { grid-template-columns: repeat(2, 1fr); }
          .sbr-card { width: 175px; }
          .sbr-section { padding: 56px 0 50px; }
        }
      `}</style>

      <section className="sbr-section" ref={sectionRef}>

        {/* Header */}
        <div className="sbr-header">
          <div>
            <p className="sbr-label">Viral Picks</p>
            <h2 className="sbr-title">Shop by <em>Reel</em></h2>
          </div>
          <Link to="/reels" className="sbr-viewall">View All</Link>
        </div>

        {/* Scroll track */}
        {loading ? (
          <div className="sbr-loading">Loading reels…</div>
        ) : reels.length === 0 ? (
          <div className="sbr-loading">No reels yet.</div>
        ) : (
          <div
            className="sbr-overflow"
            onMouseEnter={() => { pauseRef.current = true; }}
            onMouseLeave={() => { if (!activeReel) pauseRef.current = false; }}
          >
            <div className="sbr-track" ref={trackRef}>
              {DOUBLED.map((r, i) => (
                <ReelCardItem
                  key={`${r.id}-${i}`}
                  r={r}
                  loadingProducts={loadingProducts}
                  onShopNow={handleShopNow}
                  modalOpen={!!activeReel}
                />
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ══ Products Modal ══ */}
      <AnimatePresence>
        {activeReel && (
          <motion.div
            className="srm-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="srm-modal"
              initial={{ opacity: 0, y: 32, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 32, scale: 0.96 }}
              transition={{ duration: 0.32, ease: [0.25, 0.46, 0.45, 0.94] }}
              onClick={e => e.stopPropagation()}
            >
              {/* Head */}
              <div className="srm-head">
                <div className="srm-head-left">
                  <p className="srm-eyebrow">Shop the Reel</p>
                  <h3 className="srm-head-title">{activeReel.title}</h3>
                </div>
                <button className="srm-close" onClick={closeModal} aria-label="Close">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="14" height="14">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              {/* Body */}
              <div className="srm-body">
                {activeReel.products.length === 0 ? (
                  <div className="srm-empty">No products linked to this reel yet.</div>
                ) : (
                  <div className="srm-grid">
                    {activeReel.products.map(p => {
                      const hasDiscount  = p.sale_price && p.sale_price < p.original_price;
                      const displayPrice = hasDiscount ? p.sale_price : p.original_price;
                      const mrpPrice     = hasDiscount ? p.original_price : null;
                      const discount     = mrpPrice
                        ? Math.round((1 - displayPrice / mrpPrice) * 100)
                        : 0;

                      const BADGE_COLORS = {
                        New:        { bg: '#1C1A18', color: '#fff' },
                        Bestseller: { bg: '#B08D57', color: '#fff' },
                        Sale:       { bg: '#C0392B', color: '#fff' },
                        Limited:    { bg: '#2C2C2C', color: '#D4AF78' },
                        Trending:   { bg: '#B08D57', color: '#fff' },
                        Hot:        { bg: '#C0392B', color: '#fff' },
                        Exclusive:  { bg: '#1C1A18', color: '#D4AF78' },
                      };
                      const bc = p.badge
                        ? (BADGE_COLORS[p.badge] || { bg: '#1C1A18', color: '#fff' })
                        : null;

                      return (
                        <div
                          key={p.id}
                          className="srm-card"
                          onClick={() => {
                          closeModal();
                          navigate(`/reels/${activeReel.id}/${p.id}`);
                        }}
                        >
                          <div className="srm-card-img">
                            <img src={p.images?.[0] || p.image_1} alt={p.name} loading="lazy" />
                            {bc && (
                              <span
                                className="srm-card-badge"
                                style={{ background: bc.bg, color: bc.color }}
                              >
                                {p.badge}
                              </span>
                            )}
                          </div>
                          <div className="srm-card-body">
                            <div className="srm-card-name">{p.name}</div>
                            <div className="srm-card-prices">
                              <span className="srm-card-price">
                                {formatPrice(displayPrice)}
                              </span>
                              {mrpPrice && (
                                <span className="srm-card-mrp">
                                  {formatPrice(mrpPrice)}
                                </span>
                              )}
                              {discount > 0 && (
                                <span className="srm-card-off">{discount}% Off</span>
                              )}
                            </div>
                            <button
                              className="srm-card-btn"
                              onClick={e => {
                              e.stopPropagation();
                              closeModal();
                              navigate(`/reels/${activeReel.id}/${p.id}`);
                            }}
                            >
                              View Product
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="srm-foot">
                <p className="srm-foot-count">
                  <span>{activeReel.products.length}</span> products in this reel
                </p>
                <Link to={`/reels/${activeReel.id}`} className="srm-view-all" onClick={closeModal}>
                  View Full Reel Page →
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ShopByReel;